import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Map from "../Components/Maps/HeatMap";
import SocketContext from "../Contexts/SocketContext";
import DateRangePicker from "../Components/Calendars/DateRangePicker";
import moment from "moment";
import OsSystemSelect from "../Components/FormElements/OsSystemSelect";
import Spinner from "../Components/Loaders/Spinner";
import Card from "../Components/Card";

// Der Server schickt Zellen kompakt:
//   { mode, cellSize, cells: [[x, y, hit_count, wert], ...] }
// x/y ist die linke untere Ecke, cellSize die Kantenlaenge in Grad - das
// Quadrat bauen wir hier. `wert` ist bei mode "single" die Distanz in km, bei
// "multi" das Array der OS-Kennzahlen.
//
// cellSize haengt vom Zoom ab: Herausgezoomt fasst der Server mehrere
// 1-Grad-Zellen zusammen. Damit die Farbskala ueber alle Zoomstufen dieselbe
// Bedeutung behaelt, faerbt die Karte nach `density` (Treffer pro Quadratgrad)
// statt nach der absoluten Summe. Bei cellSize 1 sind beide identisch, das
// Bild bei voller Aufloesung bleibt also unveraendert.
const toFeatureCollection = ({ mode, cellSize, cells }) => {
  const area = cellSize * cellSize;

  return {
    type: "FeatureCollection",
    features: cells.map(([x, y, hitCount, value]) => ({
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [x, y],
            [x + cellSize, y],
            [x + cellSize, y + cellSize],
            [x, y + cellSize],
            [x, y],
          ],
        ],
      },
      // Mapbox reicht verschachtelte Properties als JSON-String zurueck. Das
      // Popup ruft darauf JSON.parse auf - deshalb hier explizit serialisieren,
      // statt sich auf dieses Verhalten zu verlassen.
      properties:
        mode === "multi"
          ? {
              hit_count: hitCount,
              density: hitCount / area,
              os_data: JSON.stringify(value),
            }
          : { hit_count: hitCount, density: hitCount / area, total_length: value },
    })),
  };
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

// Mapbox liefert beim Herauszoomen oder ueber die Datumsgrenze hinweg auch
// Werte ausserhalb von [-180, 180]. ST_MakeEnvelope wuerde daraus eine leere
// oder invertierte Box bauen - in dem Fall lieber die ganze Welt anfragen.
const boundsToEnvelope = (map) => {
  const bounds = map?.getBounds?.();
  if (!bounds) return null;

  const west = clamp(bounds.getWest(), -180, 180);
  const east = clamp(bounds.getEast(), -180, 180);
  const south = clamp(bounds.getSouth(), -90, 90);
  const north = clamp(bounds.getNorth(), -90, 90);

  if (west >= east || south >= north) {
    return { west: -180, south: -90, east: 180, north: 90 };
  }
  return { west, south, east, north };
};

// Ohne Antwort haengt sonst der Ladezustand unbegrenzt. Ein Zustand, aus dem
// die Oberflaeche nicht mehr herausfindet, darf es nicht geben.
const REQUEST_TIMEOUT_MS = 20000;

const Heatmap = () => {
  const mapRef = useRef(null);
  // Antwort, die eintraf, bevor die Kartenquelle existierte. Wird nachgezogen,
  // sobald die Karte bereit ist.
  const pendingDataRef = useRef(null);
  const timeoutRef = useRef(null);

  const [selectedDateRange, setSelectedDateRange] = useState({
    start: new Date(moment().subtract(7, "days").format("YYYY-MM-DD")),
    end: new Date(moment().format("YYYY-MM-DD")),
  });
  const [selectedOsSystem, setSelectedOsSystem] = useState(null);
  const socket = useContext(SocketContext);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const applyData = useCallback((featureCollection) => {
    const source = mapRef.current?.getSource("heatmap");
    if (!source) {
      pendingDataRef.current = featureCollection;
      return;
    }
    source.setData(featureCollection);
    pendingDataRef.current = null;
  }, []);

  // Eigener Effekt fuer den Listener, damit er genau einmal registriert und
  // beim Aufraeumen wieder entfernt wird. Vorher hing das an denselben
  // Dependencies wie die Anfrage - jede Zeitraum- oder OS-Aenderung legte einen
  // weiteren Listener obendrauf, und jedes Payload wurde entsprechend
  // mehrfach verarbeitet.
  useEffect(() => {
    if (!socket) return undefined;

    const handleHeatmapData = (payload) => {
      clearTimeout(timeoutRef.current);
      try {
        applyData(toFeatureCollection(payload));
        setError(null);
      } catch (err) {
        setError("Heatmap-Daten konnten nicht gelesen werden.");
      }
      setLoading(false);
    };

    socket.on("heatmapData", handleHeatmapData);
    return () => socket.off("heatmapData", handleHeatmapData);
  }, [socket, applyData]);

  // Die Anfrage haengt bewusst NICHT daran, dass die Karte fertig ist: Feuert
  // deren load-Event aus irgendeinem Grund nicht, wuerde sonst nie etwas
  // angefragt und der Ladezustand bliebe fuer immer stehen. Kommen die Daten
  // vor der Karte an, puffert applyData sie.
  const requestHeatmap = useCallback(() => {
    if (!socket) return;

    setLoading(true);
    setError(null);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setLoading(false);
      setError("Keine Antwort vom Server erhalten.");
    }, REQUEST_TIMEOUT_MS);

    try {
      socket.emit(
        "showHeatmap",
        selectedDateRange,
        selectedOsSystem,
        boundsToEnvelope(mapRef.current)
      );
    } catch (err) {
      clearTimeout(timeoutRef.current);
      setLoading(false);
      setError("Anfrage konnte nicht gesendet werden.");
    }
  }, [socket, selectedDateRange, selectedOsSystem]);

  // Einmal sobald der Socket steht (ohne Viewport, der Server nimmt dann die
  // Weltansicht), und erneut sobald die Karte bereit ist - dann mit Bounds.
  useEffect(() => {
    requestHeatmap();
  }, [requestHeatmap, mapLoaded]);

  useEffect(() => {
    if (mapLoaded && pendingDataRef.current) {
      applyData(pendingDataRef.current);
    }
  }, [mapLoaded, applyData]);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  // Da der Server jetzt auf den Viewport filtert, muss beim Verschieben und
  // Zoomen nachgeladen werden - sonst fehlten beim Herauszoomen Zellen.
  // Entprellt, damit ein Drag nicht in eine Anfrageflut mündet.
  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return undefined;

    let timer = null;
    const handleMoveEnd = () => {
      clearTimeout(timer);
      timer = setTimeout(requestHeatmap, 400);
    };

    map.on("moveend", handleMoveEnd);
    return () => {
      clearTimeout(timer);
      map.off("moveend", handleMoveEnd);
    };
  }, [mapLoaded, requestHeatmap]);

  const handleMapLoad = () => {
    setMapLoaded(true);
  };

  return (
    <>
      <div className="md:flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-bold mb-4">Heatmap</h1>
          <p className="mb-8 text-sm text-gray-600 whitespace-pre-line">
            {`This heatmap visualizes the distribution of aircraft across the globe over the past 7 Days. \nDenser colors represent areas with higher concentrations of flights.`}
          </p>
        </div>
        <div className="flex justify-end mb-4 space-x-4">
          <OsSystemSelect
            onSelect={setSelectedOsSystem}
            showAllOption
            defaultSelect={null}
          />
          <DateRangePicker
            onDateSelect={setSelectedDateRange}
            defaultRange={{
              startDate: new Date(
                moment().subtract(7, "days").format("YYYY-MM-DD")
              ),
              endDate: new Date(moment().format("YYYY-MM-DD")),
              key: "selection",
            }}
          />
        </div>
      </div>
      <div className="h-full w-full relative pb-12">
        {loading && (
          <div className="absolute h-full w-full bg-slate-500 bg-opacity-40 z-50 flex items-center justify-center rounded-lg">
            <div className="h-32 w-32">
              <Card>
                <div className="flex items-center justify-center flex-shrink-0 h-full">
                  <Spinner height={"h-12"} />
                </div>
              </Card>
            </div>
          </div>
        )}
        {!loading && error && (
          <div className="absolute h-full w-full bg-slate-500 bg-opacity-40 z-50 flex items-center justify-center rounded-lg">
            <Card>
              <div className="px-4 py-2 text-center">
                <p className="text-sm font-semibold mb-2">{error}</p>
                <button
                  type="button"
                  onClick={requestHeatmap}
                  className="bg-primary px-4 py-2 rounded-lg text-white text-sm font-semibold"
                >
                  Erneut versuchen
                </button>
              </div>
            </Card>
          </div>
        )}
        <Map
          selectedOsSystem={selectedOsSystem}
          ref={mapRef}
          onLoad={handleMapLoad}
          start={selectedDateRange.start}
          end={selectedDateRange.end}
        />
      </div>
    </>
  );
};

export default Heatmap;
